import { fetchAnalyticsCountsAPIPath, fetchAnalyticsTimeSeriesAPIPath } from "../links"
import { getDateFromRelativeString, getDateString } from "../utils"

export async function updateSettings(csrfToken, settings) {
  try {
    let res = await fetch(`/api/admin/organization/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(settings)
    })
    let data = await res.json()
    return {
      error: data.Error,
    }
  } catch (e) {
    return {
      error: "Error while updating settings. Please try again in sometime."
    }
  }
}

export async function getLogoPresignedUrl(csrfToken) {
  try {
    let res = await fetch('/api/admin/organization/logo/presigned-url', {
      method: 'GET',
      headers: {
        'X-CSRF-Token': csrfToken,
      },
    })
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error }
    }
    return data
  } catch (e) {
    return { error: "Error while fetching upload URL. Please try again." }
  }
}

export async function updateLogo(csrfToken, s3Key) {
  try {
    let res = await fetch('/api/admin/organization/logo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ S3Key: s3Key }),
    })
    let data = await res.json()
    return { error: data.Error }
  } catch (e) {
    return { error: "Error while updating logo. Please try again." }
  }
}

export async function deleteLogo(csrfToken) {
  try {
    let res = await fetch('/api/admin/organization/logo', {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken,
      },
    })
    let data = await res.json()
    return { error: data.Error }
  } catch (e) {
    return { error: "Error while deleting logo. Please try again." }
  }
}

export async function disconnectSlack(csrfToken) {
  try {
    let res = await fetch(`/api/admin/slack/disconnect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    let data = await res.json()
    return {
      error: data.Error,
    }
  } catch (e) {
    return {
      error: "Error while disconnecting Slack. Please try again in sometime."
    }
  }
}

// disconnectTeams disconnects Microsoft Teams from the organization, or from the one a super admin is viewing.
export async function disconnectTeams(csrfToken, { orgID } = {}) {
  try {
    let res = await fetch(`/api/admin/teams/disconnect${orgQuery(orgID)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    let data = await res.json()
    return {
      error: data.Error,
    }
  } catch (e) {
    return {
      error: "Error while disconnecting Microsoft Teams. Please try again in sometime."
    }
  }
}

export async function fetchAnalyticsSummaryData(params = {}) {
  try {
    const queryParams = new URLSearchParams(params)
    let res = await fetch(`/api/admin/organization/analytics-summary?${queryParams.toString()}`)
    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }
    return {
      analytics: data.Data,
    }
  } catch (e) {
    return {
      error: "Error while updating settings. Please try again in sometime."
    }
  }
}

export async function createChannel(csrfToken, channel) {
  try {
    let res = await fetch(`/api/admin/channels`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(channel)
    })
    let data = await res.json()
    return {
      error: data.Error,
    }
  } catch (e) {
    return {
      error: "Error while creating channel. Please try again in sometime."
    }
  }
}

export async function fetchChannels(params = {}) {
  const queryParams = new URLSearchParams(params)
  try {
    let res = await fetch(`/api/admin/channels?${queryParams.toString()}`)
    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }
    return {
      channels: data.Data.Channels,
    }
  } catch (e) {
    return {
      error: "Error while fetching channels. Please try again in sometime."
    }
  }
}

export async function updateChannel(csrfToken, channel) {
  try {
    let res = await fetch(`/api/admin/channels/${channel.ID}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(channel)
    })

    if (res.status === 204) {
      return {}
    }

    if (res.status === 404) {
      return {
        error: "Channel not found. Please refresh the page."
      }
    }

    if (res.status === 403) {
      return {
        error: "You do not have permission to update this channel."
      }
    }

    let data = await res.json()
    return {
      error: data.Error,
    }
  } catch (e) {
    return {
      error: "Error while updating channel. Please try again in sometime."
    }
  }
}

export async function deleteChannel(csrfToken, channel) {
  try {
    let res = await fetch(`/api/admin/channels/${channel.ID}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    if (res.status === 204) {
      return {}
    }

    if (res.status === 404) {
      return {
        error: "Channel not found. Please refresh the page."
      }
    }

    if (res.status === 403) {
      return {
        error: "You do not have permission to delete this channel."
      }
    }

    return {
      error: "Error while deleting channel. Please try again in sometime."
    }
  } catch (e) {
    return {
      error: "Error while deleting channel. Please try again in sometime."
    }
  }
}

function orgQuery(orgID) {
  return orgID ? `?${new URLSearchParams({ orgID }).toString()}` : ""
}

// fetchAIUsage returns the AI usage of the organization over the last day and week. Only super admins can read it.
export async function fetchAIUsage({ orgID } = {}) {
  try {
    let res = await fetch(`/api/admin/ai-usage${orgQuery(orgID)}`)
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error || "Error while fetching AI usage. Please try again in sometime." }
    }
    return { usage: data.Data }
  } catch (e) {
    return { error: "Error while fetching AI usage. Please try again in sometime." }
  }
}

export async function fetchCampaigns({ orgID } = {}) {
  try {
    let res = await fetch(`/api/admin/campaigns${orgQuery(orgID)}`)
    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }
    return {
      campaigns: data.Data.Campaigns,
    }
  } catch (e) {
    return {
      error: "Error while fetching campaigns. Please try again in sometime."
    }
  }
}

// fetchAdministeredLinkedinOrganizations returns the LinkedIn company pages the current admin administers
export async function fetchAdministeredLinkedinOrganizations({ orgID } = {}) {
  try {
    let res = await fetch(`/api/admin/linkedin/administered-organizations${orgQuery(orgID)}`)
    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }
    return {
      organizations: data.Data.Organizations || [],
    }
  } catch (e) {
    return {
      error: "Error while fetching your LinkedIn company pages. Please try again in sometime."
    }
  }
}

export async function addCompanyPage(csrfToken, { linkedinOrganizationID, pageType }, orgID) {
  try {
    let res = await fetch(`/api/admin/company-pages${orgQuery(orgID)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ LinkedinOrganizationID: linkedinOrganizationID, PageType: pageType })
    })
    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }
    return {
      companyPage: data.Data,
    }
  } catch (e) {
    return {
      error: "Error while adding company page. Please try again in sometime."
    }
  }
}

export async function removeCompanyPage(csrfToken, companyPage, orgID) {
  try {
    let res = await fetch(`/api/admin/company-pages/${companyPage.ID}${orgQuery(orgID)}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
    if (res.status === 204) {
      return {}
    }
    let data = await res.json()
    return {
      error: data.Error || "Error while removing company page. Please try again in sometime.",
    }
  } catch (e) {
    return {
      error: "Error while removing company page. Please try again in sometime."
    }
  }
}

export async function createCampaign(csrfToken, campaign, orgID) {
  try {
    let res = await fetch(`/api/admin/campaigns${orgQuery(orgID)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(campaign)
    })
    let data = await res.json()
    return {
      error: data.Error,
    }
  } catch (e) {
    return {
      error: "Error while creating campaign. Please try again in sometime."
    }
  }
}

export async function updateCampaign(csrfToken, campaign, orgID) {
  try {
    let res = await fetch(`/api/admin/campaigns/${campaign.ID}${orgQuery(orgID)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(campaign)
    })

    if (res.status === 404) {
      return {
        error: "Campaign not found. Please refresh the page."
      }
    }

    if (res.status === 401 || res.status === 403) {
      return {
        error: "You do not have permission to update this campaign."
      }
    }

    let data = await res.json()
    return {
      error: data.Error,
    }
  } catch (e) {
    return {
      error: "Error while updating campaign. Please try again in sometime."
    }
  }
}

export async function deleteCampaign(csrfToken, campaign, orgID) {
  try {
    let res = await fetch(`/api/admin/campaigns/${campaign.ID}${orgQuery(orgID)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    if (res.status === 204) {
      return {}
    }

    if (res.status === 404) {
      return {
        error: "Campaign not found. Please refresh the page."
      }
    }

    if (res.status === 401 || res.status === 403) {
      return {
        error: "You do not have permission to delete this campaign."
      }
    }

    return {
      error: "Error while deleting campaign. Please try again in sometime."
    }
  } catch (e) {
    return {
      error: "Error while deleting campaign. Please try again in sometime."
    }
  }
}

async function updateCampaignPublicLink(method, csrfToken, campaign, orgID) {
  try {
    let res = await fetch(`/api/admin/campaigns/${campaign.ID}/public-link${orgQuery(orgID)}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error || "Error while updating the public link. Please try again in sometime." }
    }
    return { campaign: data.Data }
  } catch (e) {
    return {
      error: "Error while updating the public link. Please try again in sometime."
    }
  }
}

// enableCampaignPublicLink creates the public link of the campaign, or regenerates it when it already exists
export async function enableCampaignPublicLink(csrfToken, campaign, orgID) {
  return updateCampaignPublicLink('POST', csrfToken, campaign, orgID)
}

export async function disableCampaignPublicLink(csrfToken, campaign, orgID) {
  return updateCampaignPublicLink('DELETE', csrfToken, campaign, orgID)
}

// appendCampaignIDs adds the campaigns to filter the analytics by, as repeated CampaignIDs params
function appendCampaignIDs(params, campaignIDs) {
  (campaignIDs || []).forEach(id => params.append('CampaignIDs', id))
}

// appendCompanyPageIDs adds the company pages to filter the analytics by, as repeated CompanyPageIDs params
function appendCompanyPageIDs(params, companyPageIDs) {
  (companyPageIDs || []).forEach(id => params.append('CompanyPageIDs', id))
}

export async function fetchTopPosts({ start, end, orgID, limit, offset, sortOrder, profileType, companyPageIDs, campaignIDs }) {
  try {
    const params = new URLSearchParams({
      orgID,
      Limit: limit,
      Offset: offset,
      "SortOrder.Field": sortOrder.Field,
      "SortOrder.Direction": sortOrder.Direction,
      TimePeriodStart: getDateString(getDateFromRelativeString(start)),
      TimePeriodEnd: getDateString(getDateFromRelativeString(end)),
    })
    if (profileType) params.append('ProfileType', profileType)
    appendCompanyPageIDs(params, companyPageIDs)
    appendCampaignIDs(params, campaignIDs)
    let res = await fetch(`/api/admin/organization/analytics/top-posts?${params.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      Posts: data.Data,
    }
  } catch (e) {
    return {
      error: "Error while fetching top posts. Please try again in sometime."
    }
  }
}

export async function fetchTopShares({ orgID, postID, advocateID, limit, offset, sortOrder, start, end }) {
  try {
    const params = new URLSearchParams({})
    if (postID) {
      params.append("postID", postID)
    }
    if (advocateID) {
      params.append("advocateID", advocateID)
    }
    if (limit) {
      params.append("Limit", limit)
    }
    if (offset) {
      params.append("Offset", offset)
    }
    if (sortOrder) {
      params.append("SortOrder.Field", sortOrder.Field)
      params.append("SortOrder.Direction", sortOrder.Direction)
    }
    if (orgID) {
      params.append("orgID", orgID)
    }
    if (start && end) {
      params.append("TimePeriodStart", getDateString(getDateFromRelativeString(start)))
      params.append("TimePeriodEnd", getDateString(getDateFromRelativeString(end)))
    }

    let res = await fetch(`/api/admin/organization/analytics/top-shares?${params.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      Shares: data.Data,
    }
  } catch (e) {
    return {
      error: "Error while fetching top shares. Please try again in sometime."
    }
  }
}

export async function fetchLinkedinUsers({ searchTerm }) {
  try {
    const params = new URLSearchParams({ searchTerm })
    let res = await fetch(`/api/linkedin/users?${params.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      Users: data.Data || [],
    }
  } catch (e) {
    return {
      error: "Error while fetching linkedin users. Please try again in sometime."
    }
  }
}

export async function fetchLinkedinOrganization({ vanityName, orgID }) {
  try {
    const params = new URLSearchParams({ vanityName })
    if (orgID) params.set('orgID', orgID)
    let res = await fetch(`/api/linkedin/organizations?${params.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      Organization: data.Data || {},
    }
  } catch (e) {
    return {
      error: "Error while fetching linkedin organization. Please try again in sometime."
    }
  }
}

export async function fetchLinkedinOrganizationPosts({ companyPageID, orgID, limit, offset } = {}) {
  try {
    const params = new URLSearchParams()
    if (companyPageID) params.set('companyPageID', companyPageID)
    if (orgID) params.set('orgID', orgID)
    if (limit != null) params.set('limit', limit)
    if (offset != null) params.set('offset', offset)
    const query = params.toString()
    let res = await fetch(`/api/admin/linkedin/posts${query ? `?${query}` : ''}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
        errorCode: res.status, // Temporary addition of status code to handle specific errors in frontend until API errors are standardized
      }
    }

    return {
      posts: data.Data || [],
    }
  } catch (e) {
    return {
      error: "Error while fetching linkedin organization posts. Please try again in sometime."
    }
  }
}

export async function fetchLinkedinOrganizationPost({ companyPostID }) {
  if (!companyPostID) {
    return {
      post: {},
    }
  }
  try {
    let res = await fetch(`/api/linkedin/posts/${companyPostID}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      post: data.Data || {},
    }
  } catch (e) {
    return {
      error: "Error while fetching linkedin organization post. Please try again in sometime."
    }
  }
}

export async function fetchSocialListeningPosts({ orgID, limit, offset } = {}) {
  try {
    const params = new URLSearchParams()
    if (orgID) params.set('orgID', orgID)
    if (limit != null) params.set('limit', limit)
    if (offset != null) params.set('offset', offset)
    const query = params.toString()
    let res = await fetch(`/api/admin/social-listening/posts${query ? `?${query}` : ''}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
        errorCode: res.status,
      }
    }

    return {
      posts: data.Data || [],
    }
  } catch (e) {
    return {
      error: "Error while fetching LinkedIn posts. Please try again in sometime."
    }
  }
}

export async function fetchSocialListeningPost({ orgID, postID }) {
  if (!postID) {
    return {
      post: null,
    }
  }
  try {
    const params = new URLSearchParams()
    if (orgID) params.set('orgID', orgID)
    const query = params.toString()
    let res = await fetch(`/api/admin/social-listening/posts/${postID}${query ? `?${query}` : ''}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      post: data.Data || null,
    }
  } catch (e) {
    return {
      error: "Error while fetching LinkedIn post. Please try again in sometime."
    }
  }
}

// fetchSocialListeningPostSourceURL returns the first link in the LinkedIn post, like a lnkd.in link, and the url it points to.
// Both are empty when the post has no link.
export async function fetchSocialListeningPostSourceURL({ orgID, postID }) {
  if (!postID) {
    return {
      sourceURL: null,
    }
  }
  try {
    const params = new URLSearchParams()
    if (orgID) params.set('orgID', orgID)
    const query = params.toString()
    let res = await fetch(`/api/admin/social-listening/posts/${postID}/source-url${query ? `?${query}` : ''}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      sourceURL: data.Data || null,
    }
  } catch (e) {
    return {
      error: "Error while resolving the link in the LinkedIn post. Please try again in sometime."
    }
  }
}

export async function fetchSocialListeningSummaries({ orgID, createdAfter, createdBefore } = {}) {
  try {
    const params = new URLSearchParams()
    if (orgID) params.set('orgID', orgID)
    if (createdAfter) params.set('createdAfter', createdAfter)
    if (createdBefore) params.set('createdBefore', createdBefore)
    const query = params.toString()
    let res = await fetch(`/api/admin/social-listening/summaries${query ? `?${query}` : ''}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
        errorCode: res.status,
      }
    }

    return {
      summaries: data.Data || [],
    }
  } catch (e) {
    return {
      error: "Error while fetching LinkedIn summaries. Please try again in sometime."
    }
  }
}

export async function createUser(csrfToken, user) {
  try {
    let res = await fetch(`/api/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(user),
    })
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error }
    }
    return { user: data.Data }
  } catch (e) {
    return { error: "Error while creating user. Please try again." }
  }
}

export async function deleteUser(csrfToken, user) {
  try {
    let res = await fetch(`/api/admin/users/${user.ID}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    if (res.status === 200) {
      return {}
    }

    if (res.status === 400) {
      let data = await res.json()
      return {
        error: data.Error
      }
    }

    if (res.status === 404) {
      return {
        error: "User not found. Please refresh the page."
      }
    }

    if (res.status === 403) {
      return {
        error: "You do not have permission to delete this user."
      }
    }

    return {
      error: "Error while deleting user. Please try again in sometime."
    }
  } catch (e) {
    return {
      error: "Error while deleting user. Please try again in sometime."
    }
  }
}

async function updateUserPersonalBrandVoice(csrfToken, user, method, defaultError) {
  try {
    let res = await fetch(`/api/admin/users/${user.ID}/personal-brand-voice`, {
      method,
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
    if (res.status === 200) {
      return {}
    }

    if (res.status === 400 || res.status === 404) {
      let data = await res.json()
      return { error: data.Error }
    }

    return { error: defaultError }
  } catch (e) {
    return { error: defaultError }
  }
}

export async function generateUserPersonalBrandVoice(csrfToken, user) {
  return updateUserPersonalBrandVoice(csrfToken, user, 'POST', "Error while generating personal brand voice. Please try again in sometime.")
}

export async function deleteUserPersonalBrandVoice(csrfToken, user) {
  return updateUserPersonalBrandVoice(csrfToken, user, 'DELETE', "Error while deleting personal brand voice. Please try again in sometime.")
}

export async function updateUserType(csrfToken, user, type) {
  try {
    let res = await fetch(`/api/admin/users/${user.ID}/type`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ type })
    })
    if (res.status === 200) {
      return {}
    }

    if (res.status === 400) {
      let data = await res.json()
      return { error: data.Error }
    }

    if (res.status === 404) {
      let data = await res.json()
      return { error: data.Error }
    }

    if (res.status === 403) {
      let data = await res.json()
      return { error: data.Error }
    }

    return { error: "Failed to update user type" }
  } catch (e) {
    return { error: "Failed to update user type" }
  }
}

export async function assignChannels(csrfToken, advocate, channels) {
  try {
    let res = await fetch(`/api/admin/advocates/${advocate?.ID}/channels`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ channels: channels })
    })
    if (res.status === 200) {
      return {}
    }

    if (res.status === 400) {
      let data = await res.json()
      return {
        error: data.Error
      }
    }

    if (res.status === 404) {
      return {
        error: "User not found. Please refresh the page."
      }
    }

    if (res.status === 403) {
      return {
        error: "You do not have permission to assign channels to this user."
      }
    }

    let data = await res.json()
    return {
      error: data.Error,
    }
  } catch (e) {
    return {
      error: "Error while assigning channels to user. Please try again in sometime."
    }
  }
}

export async function fetchOrganizationPostAnalytics({ orgID, CurrentTimePeriodStart, CurrentTimePeriodEnd, PreviousTimePeriodStart, PreviousTimePeriodEnd, ProfileType, companyPageIDs, campaignIDs }) {
  try {
    const params = new URLSearchParams({ CurrentTimePeriodStart, CurrentTimePeriodEnd, PreviousTimePeriodStart, PreviousTimePeriodEnd })
    if (orgID) params.append('orgID', orgID)
    if (ProfileType) params.append('ProfileType', ProfileType)
    appendCompanyPageIDs(params, companyPageIDs)
    appendCampaignIDs(params, campaignIDs)
    const res = await fetch(`/api/admin/analytics/posts?${params.toString()}`)
    const { Data, Error: error } = await res.json()
    if (error) return { error }
    return { postAnalytics: Data }
  } catch (e) {
    return { error: "Error while fetching post analytics. Please try again." }
  }
}

export async function fetchOrganizationUserAnalytics({ orgID, CurrentTimePeriodStart, CurrentTimePeriodEnd, PreviousTimePeriodStart, PreviousTimePeriodEnd, campaignIDs }) {
  try {
    const params = new URLSearchParams({ CurrentTimePeriodStart, CurrentTimePeriodEnd, PreviousTimePeriodStart, PreviousTimePeriodEnd })
    if (orgID) params.append('orgID', orgID)
    appendCampaignIDs(params, campaignIDs)
    const res = await fetch(`/api/admin/analytics/users?${params.toString()}`)
    const { Data, Error: error } = await res.json()
    if (error) return { error }
    return { userAnalytics: Data }
  } catch (e) {
    return { error: "Error while fetching user analytics. Please try again." }
  }
}

export async function fetchAnalytics({ postIDs, orgID }) {
  try {
    const params = new URLSearchParams({})
    if (postIDs && postIDs.length > 0) {
      params.append("postIDs", postIDs.join(","))
    }
    if (orgID) {
      params.append("orgID", orgID)
    }

    let res = await fetch(`/api/admin/analytics?${params.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      Analytics: data.Data,
    }
  } catch (e) {
    return {
      error: "Error while fetching analytics. Please try again in sometime."
    }
  }
}

export async function fetchAnalyticsTimeSeries({ postIDs, shareIDs, orgID, currentTimePeriodStart, currentTimePeriodEnd, previousTimePeriodStart, previousTimePeriodEnd, metric }) {
  try {
    let res = await fetch(fetchAnalyticsTimeSeriesAPIPath({ postIDs, shareIDs, orgID, metric, currentTimePeriodStart, currentTimePeriodEnd, previousTimePeriodStart, previousTimePeriodEnd }), {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      AnalyticsTimeSeries: data.Data,
    }
  } catch (e) {
    return {
      error: "Error while fetching analytics time series. Please try again in sometime."
    }
  }
}

export async function fetchAnalyticsCounts({ shareIDs, orgID, timePeriodStart, timePeriodEnd }) {
  try {
    let res = await fetch(fetchAnalyticsCountsAPIPath({ shareIDs, orgID, timePeriodStart, timePeriodEnd }), {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return { error: data.Error }
    }

    return { counts: data.Data }
  } catch (e) {
    return {
      error: "Error while fetching analytics counts. Please try again in sometime."
    }
  }
}

// TODO add support for super user
export async function getShareUrls(postID) {
  try {
    const res = await fetch(`/api/admin/posts/${postID}/share-urls`)
    const data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error }
    }
    return { shareUrls: data.Data }
  } catch (e) {
    return { error: 'Error while fetching share URLs. Please try again.' }
  }
}

export async function deleteShareUrl(csrfToken, postID, shareUrlID) {
  try {
    const res = await fetch(`/api/admin/posts/${postID}/share-urls/${shareUrlID}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken },
    })
    if (res.status >= 400) {
      const data = await res.json()
      return { error: data.Error }
    }
    return {}
  } catch (e) {
    return { error: 'Error while deleting share URL. Please try again.' }
  }
}

export async function createShareUrl(csrfToken, postID, shareTargetType, channelIds = []) {
  try {
    const res = await fetch(`/api/admin/posts/${postID}/share-urls`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ ShareTargetType: shareTargetType, ChannelIds: channelIds }),
    })
    const data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error }
    }
    return { shareUrl: data.Data }
  } catch (e) {
    return { error: 'Error while generating share URL. Please try again.' }
  }
}

export async function bulkImportShares(csrfToken, advocateID, shares) {
  try {
    const res = await fetch('/api/admin/social-media-shares/bulk-import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ AdvocateID: advocateID, Shares: shares }),
    })
    const data = await res.json()
    return { error: data.Error }
  } catch (e) {
    return { error: 'Error while importing shares. Please try again.' }
  }
}

export async function getLatestImportBatches(advocateIDs) {
  try {
    const params = new URLSearchParams()
    advocateIDs.forEach(id => params.append('advocateIDs', id))
    const res = await fetch(`/api/admin/social-media-shares/import-batches/latest?${params}`)
    const data = await res.json()
    return { data: data.Data, error: data.Error }
  } catch (e) {
    return { error: 'Error fetching import batch statuses.' }
  }
}

// fetchSocialListeningPostMedias copies the video or document of the LinkedIn post, else its images, to medias which can be used in a post
export async function fetchSocialListeningPostMedias({ orgID, postID }) {
  if (!postID) {
    return {
      medias: [],
    }
  }
  try {
    const params = new URLSearchParams()
    if (orgID) params.set('orgID', orgID)
    const query = params.toString()
    let res = await fetch(`/api/admin/social-listening/posts/${postID}/medias${query ? `?${query}` : ''}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    })

    let data = await res.json()
    if (data.Error) {
      return {
        error: data.Error,
      }
    }

    return {
      medias: data.Data || [],
    }
  } catch (e) {
    return {
      error: "Error while copying the media of the LinkedIn post. Please try again in sometime."
    }
  }
}

// fetchCalendarItems returns the leadership activation and company page shares between from and to
export async function fetchCalendarItems({ orgID, from, to }) {
  try {
    const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() })
    if (orgID) {
      query.set("orgID", orgID)
    }
    let res = await fetch(`/api/admin/calendar?${query.toString()}`)
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error || "Error while fetching the calendar. Please try again in sometime." }
    }
    return { items: data.Data?.Items || [] }
  } catch (e) {
    console.error("error while fetching the calendar", e)
    return { error: "Error while fetching the calendar. Please try again in sometime." }
  }
}

// fetchTeamsChannels returns the channels of the Microsoft Teams team the organization added Ambo to.
export async function fetchTeamsChannels({ orgID } = {}) {
  try {
    let res = await fetch(`/api/admin/teams/channels${orgQuery(orgID)}`)
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error || "Error while fetching Microsoft Teams channels. Please try again in sometime." }
    }
    return { channels: data.Data?.Channels || [] }
  } catch (e) {
    return { error: "Error while fetching Microsoft Teams channels. Please try again in sometime." }
  }
}

// updateTeamsChannel saves the Microsoft Teams channel notifications are posted to.
export async function updateTeamsChannel(csrfToken, channelID, { orgID } = {}) {
  try {
    let res = await fetch(`/api/admin/teams/channel${orgQuery(orgID)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ ChannelID: channelID })
    })
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error || "Error while saving the channel. Please try again in sometime." }
    }
    return { channel: data.Data }
  } catch (e) {
    return { error: "Error while saving the channel. Please try again in sometime." }
  }
}
